import { useState, useEffect, useCallback, useRef, ChangeEvent } from "react";
import {
  useParams,
  Link,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useBrandsApi } from "@/hooks/useBrandsApi";
import { useBranchesApi } from "@/hooks/useBranchesApi";
import LoadingSpinner from "@/components/orders/LoadingSpinner";
import ErrorMessage from "@/components/orders/ErrorMessage";
import { toastHandler } from "@/lib/ToastHandler";
import GoogleMapWrapper from "@/components/GoogleMapWrapper";
import { DrawingManager } from "@react-google-maps/api";
import { LocationPicker } from "@/components/LocationPicker";
import BranchDriverAssignmentConfirmation from "@/components/branches/BranchDriverAssignmentConfirmation";
import {
  Bike,
  CheckCircle2,
  ChevronRight,
  Loader2,
  Pencil,
  Store,
  XCircle,
} from "lucide-react";
import { Card } from "@/components/atoms/card";
import { Button } from "@/components/atoms/button";
import { EmptyState } from "@/components/atoms/empty-state";
import { useDriversApi } from "@/hooks/useDriversApi";
import OrdersTable from "@/components/orders/OrdersTable";
import DriverModal from "@/components/modals/DriverModal";
import { Driver } from "@/types/supabase";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/atoms/dialog";
import Pagination from "@/components/atoms/Pagination";
import { z } from "zod";
import { usePermissions } from "@/contexts/PermissionContext";
import { useAuth } from "@/hooks/useAuth";
import { BranchAccessDenied } from "@/components/access-control/AccessDenied";
import { CountryBadge } from "../../Brand";
import { getDefaultCoordsForCountry } from "@/lib/googleMapsConfig";
import { useAuth0 } from "@auth0/auth0-react";

type BranchFormData = {
  id: number;
  name: string;
  code: string;
  is_active: boolean;
  order_grouping_enabled: boolean;
  country_code: string;
  phone: string;
  full_address: string;
  location_lon: number;
  location_lat: number;
  brand_id: number;
  created_at?: string;
  polygon?: google.maps.LatLngLiteral[];
};

export const branchSchema = z.object({
  code: z
    .string()
    .min(1, "El código es requerido")
    .max(100, "Máximo 100 caracteres"),
  name: z
    .string()
    .min(1, "El nombre es requerido")
    .max(100, "Máximo 100 caracteres"),
  country_code: z
    .string()
    .min(1, "El código de país es requerido")
    .regex(
      /^\d{1,5}\+?$/,
      "Debe ser un número de 1 a 5 dígitos, puede haber +",
    ),
  phone: z
    .string()
    .min(7, "Mínimo 7 dígitos")
    .max(12, "Máximo 12 dígitos")
    .regex(/^\d+$/, "Solo números"),
  full_address: z
    .string()
    .min(1, "La dirección es requerida")
    .max(255, "Máximo 255 caracteres"),
  location_lat: z
    .number({
      required_error: "La latitud es requerida",
      invalid_type_error: "La latitud debe ser un número válido",
    })
    .min(-90, "La latitud debe estar entre -90 y 90")
    .max(90, "La latitud debe estar entre -90 y 90"),
  location_lon: z
    .number({
      required_error: "La longitud es requerida",
      invalid_type_error: "La longitud debe ser un número válido",
    })
    .min(-180, "La longitud debe estar entre -180 y 180")
    .max(180, "La longitud debe estar entre -180 y 180"),
});

// Add debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

export default function BranchDetail() {
  const { brandId, branchId } = useParams<{
    brandId: string;
    branchId?: string;
  }>();
  const [brandCountry, setBrandCountry] = useState<{
    name: string;
    short_name: string;
    flag_url: string;
    area_code: string;
  } | null>(null);
  const { brands, fetchBrandById } = useBrandsApi();
  const { hasPermission, hasBrandAccess, hasBranchAccess } = usePermissions();
  const { userRole } = useAuth();
  const {
    branches,
    error,
    createBranch,
    updateBranch,
    fetchBranchesPaginated,
    toggleOrderGrouping,
  } = useBranchesApi();
  const {
    error: errorDrivers,
    fetchDriversPaginated,
    updateDriver,
  } = useDriversApi();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const { user } = useAuth0();
  const { isManager } = useAuth();
  const isCreateMode = !branchId || branchId === "new";
  const [editMode, setEditMode] = useState(isCreateMode);
  const [saving, setSaving] = useState(false);
  const [brandName, setBrandName] = useState("");
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedDriverIds, setSelectedDriverIds] = useState<number[]>([]);
  const [isAssigning, setIsAssigning] = useState(false);
  const [branchDrivers, setBranchDrivers] = useState<Driver[]>([]);
  const [drawnPolygon, setDrawnPolygon] = useState<google.maps.LatLngLiteral[]>(
    [],
  );
  const [currentPolygon, setCurrentPolygon] =
    useState<google.maps.Polygon | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(
    null,
  );
  const [originalBranchData, setOriginalBranchData] =
    useState<BranchFormData | null>(null);
  const [polygonDirty, setPolygonDirty] = useState(false);

  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Access control: Check if user has permission to view branches/businesses
  const canViewBranches =
    hasPermission("branch:read") || hasPermission("business:read");
  const hasAccessToBrand = brandId ? hasBrandAccess(Number(brandId)) : false;
  const hasAccessToBranch =
    branchId && branchId !== "new"
      ? hasBranchAccess(Number(branchId))
      : hasAccessToBrand;

  // For managers, check if they have access to this specific brand/branch
  const shouldShowAccessDenied =
    !canViewBranches ||
    (userRole === "manager" && (!hasAccessToBrand || !hasAccessToBranch));

  const PAGE_SIZE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [driversTotalCount, setDriversTotalCount] = useState(0);

  // Add states for assign modal
  const [assignDriversData, setAssignDriversData] = useState<Driver[]>([]);
  const [loadingAssignDrivers, setLoadingAssignDrivers] = useState(false);

  const tableRef = useRef<HTMLDivElement>(null);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    if (tableRef.current) {
      tableRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Effect to update polygon properties when editMode changes
  useEffect(() => {
    if (currentPolygon) {
      currentPolygon.setEditable(editMode);
      currentPolygon.setDraggable(editMode);
    }
  }, [editMode, currentPolygon]);

  // Find the current branch if editing
  const branch = !isCreateMode
    ? branches.find((b) => b.id.toString() === branchId)
    : null;

  const fetchData = async () => {
    setLoading(true);
    const brand: any = await fetchBrandById(Number(brandId));
    setBrandName(brand?.name || "");
    setBrandCountry(brand?.country);
    // Only fetch branch data if not in create mode
    if (!isCreateMode && branchId) {
      const branches: any = await fetchBranchesPaginated({
        page: 1,
        limit: 100,
        brandId: Number(brandId),
        user: isManager ? user : undefined,
      });
      const branch: any = branches.data.find(
        (b: any) => b.id === Number(branchId),
      );
      setBranchData(branch);
      setOriginalBranchData(branch);
    }

    setLoading(false);
  };
  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenAssignModal = () => {
    setIsAssignModalOpen(true);
    setSelectedDriverIds([]);
    setAssignSearch("");
    setAssignDriversData([]);
    // Immediately fetch available drivers when modal opens
    setTimeout(() => {
      if (branch?.id) {
        fetchAvailableDrivers();
      }
    }, 100);
  };

  const handleCloseAssignModal = () => {
    setIsAssignModalOpen(false);
    setSelectedDriverIds([]);
    setIsAssigning(false);
    setAssignSearch("");
    setAssignDriversData([]);
  };

  const handleDriverSelect = (driverId: number) => {
    setSelectedDriverIds((prev) =>
      prev.includes(driverId)
        ? prev.filter((id) => id !== driverId)
        : [...prev, driverId],
    );
  };

  const handleConfirmDriverAssignment = async () => {
    try {
      setIsAssigning(true);

      // Check if branchData.id exists (shouldn't assign drivers in create mode)
      if (!branchData?.id) {
        toastHandler({
          title: "Error",
          description:
            "Primero debes guardar la sucursal antes de asignar conductores.",
          variant: "destructive",
        });
        setIsAssigning(false);
        return;
      }

      // Update each selected driver's branch_id
      const updatePromises = selectedDriverIds.map((driverId) =>
        updateDriver(driverId, { branch_id: branchData.id }),
      );

      await Promise.all(updatePromises);

      handleCloseAssignModal();
      toastHandler({
        title: "Éxito",
        description: `${selectedDriverIds.length} conductor(es) asignado(s) correctamente.`,
        variant: "success",
      });

      // Refresh both the branch drivers list and available drivers
      fetchBranchDrivers();
    } catch (error) {
      console.error("Error assigning drivers:", error);
      toastHandler({
        title: "Error",
        description:
          "No se pudieron asignar los conductores. Intenta de nuevo.",
        variant: "destructive",
      });
      setIsAssigning(false);
    }
  };
  // Form state (aligned with Branch type)
  const defaultsInit = getDefaultCoordsForCountry(brandCountry?.short_name);
  const [branchData, setBranchData] = useState<BranchFormData>({
    id: 0,
    name: "",
    code: "",
    is_active: true,
    order_grouping_enabled: true,
    country_code: brandCountry?.area_code?.toString() || "",
    phone: "",
    full_address: "",
    location_lon: defaultsInit.lng,
    location_lat: defaultsInit.lat,
    brand_id: Number(brandId) || 0,

    created_at: "",
  });

  const mapInstanceRef = useRef<google.maps.Map | null>(null);

  // --- BEGIN: Marker position update logic ---
  // This effect will update the marker position if the branch location changes
  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.position = {
        lat: branchData?.location_lat,
        lng: branchData?.location_lon,
      };
    }
  }, [branchData.location_lat, branchData.location_lon]);
  // --- END: Marker position update logic ---

  // Update country code when brand country changes (only in create mode or when country_code is empty/old)
  useEffect(() => {
    if (brandCountry?.area_code && (isCreateMode || !branchData.country_code)) {
      setBranchData((prev) => ({
        ...prev,
        country_code: brandCountry.area_code.toString(),
      }));
    }
  }, [brandCountry, isCreateMode]);

  const handleMapLoad = (map: google.maps.Map) => {
    mapInstanceRef.current = map;

    const googleMaps = window.google?.maps;
    const AdvancedMarker = googleMaps?.marker?.AdvancedMarkerElement;

    if (!AdvancedMarker) {
      console.warn("AdvancedMarkerElement no disponible.");
      return;
    }

    // Ícono personalizado (puede ser una URL externa o local en /public)
    const icon = document.createElement("div");
    icon.innerHTML = `
    <div style="position: relative; width: 40px; height: 40px;">
      <img 
        src="/branch-map.png" 
        alt="Sucursal" 
        style="width: 40px; height: 40px; position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);"
      />
    </div>
  `;

    // Crea el marcador avanzado
    markerRef.current = new AdvancedMarker({
      map,
      position: {
        lat: branchData?.location_lat,
        lng: branchData?.location_lon,
      },
      content: icon,
      title: branchData?.name || "Sucursal",
      collisionBehavior:
        google.maps.CollisionBehavior.OPTIONAL_AND_HIDES_LOWER_PRIORITY,
    });

    // Dibujar el polígono si existe
    if (branchData?.polygon && branchData.polygon.length > 0) {
      const polygon = new google.maps.Polygon({
        paths: branchData.polygon,
        strokeColor: "#FF0000",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#FF0000",
        fillOpacity: 0.35,
        editable: false, // Initially not editable
        draggable: false, // Initially not draggable
        clickable: false,
      });
      polygon.setMap(map);
      setCurrentPolygon(polygon);
    }
  };

  // Load brand name and branch data
  useEffect(() => {
    if (branch && !isCreateMode) {
      let polygon: { lat: number; lng: number }[] | undefined = undefined;

      // Handle direct polygon array format
      if (Array.isArray((branch as any).service_zone?.coordinates)) {
        polygon = (branch as any).service_zone?.coordinates
          .map((pt: { lat: number; lon: number }) => ({
            lat: Number(pt.lat),
            lng: Number(pt.lon),
          }))
          .filter(
            (pt: { lat: number; lng: number }) =>
              !isNaN(pt.lat) && !isNaN(pt.lng),
          );
      }
      // Handle serviceZonePolygon (legacy or direct field)
      else if ((branch as any).serviceZonePolygon) {
        try {
          const geo = JSON.parse((branch as any).serviceZonePolygon);

          if (
            geo.type === "Polygon" &&
            Array.isArray(geo.coordinates) &&
            geo.coordinates.length > 0
          ) {
            polygon = geo.coordinates[0]
              .map(([lon, lat]: [number, number]) => ({
                lat: Number(lat),
                lng: Number(lon),
              }))
              .filter(
                (pt: { lat: number; lng: number }) =>
                  !isNaN(pt.lat) && !isNaN(pt.lng),
              );
          }
        } catch (e) {
          console.error("Invalid GeoJSON:", e);
        }
      }

      const newBranchData = {
        id: branch.id,
        name: branch.name || "",
        code: branch.code || "",
        is_active: branch.is_active ?? true,
        order_grouping_enabled: (branch as any).order_grouping_enabled ?? true,
        country_code: branch.country_code || "503",
        phone: branch.phone || "",
        full_address: branch.full_address || "",
        location_lon: branch.location_lon ?? 0,
        location_lat: branch.location_lat ?? 0,
        brand_id: branch.brand_id || 0,
        created_at: branch.created_at || "",
        polygon: polygon ?? [],
      };

      setBranchData(newBranchData);
      setOriginalBranchData(newBranchData); // Store original data
    } else if (isCreateMode) {
      const countryDefaults = getDefaultCoordsForCountry(
        brandCountry?.short_name,
      );
      const newBranchData = {
        id: 0,
        name: "",
        code: "",
        is_active: true,
        order_grouping_enabled: true,
        country_code: "503",
        phone: "",
        full_address: "",
        location_lon: countryDefaults.lng,
        location_lat: countryDefaults.lat,
        brand_id: Number(brandId) || 0,
        created_at: new Date().toISOString(),
      };
      setBranchData(newBranchData);
      setOriginalBranchData(newBranchData);
    }
  }, [brands, brandId, branch, isCreateMode, brandCountry]);

  useEffect(() => {
    if (searchParams.get("edit")) {
      setEditMode(true);
      searchParams.delete("edit");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams]);

  const containerStyle = { width: "100%", height: 400 };
  const center = {
    lat: branchData?.location_lat,
    lng: branchData?.location_lon,
  };

  // Handler for when a polygon is drawn
  const handlePolygonComplete = useCallback(
    (polygon: google.maps.Polygon) => {
      if (currentPolygon) {
        currentPolygon.setMap(null);
        setCurrentPolygon(null);
      }
      const path = polygon.getPath();
      const coordinates: { lat: number; lng: number }[] = [];
      for (let i = 0; i < path.getLength(); i++) {
        const point = path.getAt(i);
        coordinates.push({ lat: point.lat(), lng: point.lng() });
      }
      setDrawnPolygon(coordinates);
      polygon.setMap(null);
      const newPolygon = new google.maps.Polygon({
        paths: coordinates,
        strokeColor: "#FF0000",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#FF0000",
        fillOpacity: 0.35,
        editable: editMode,
        draggable: editMode,
        clickable: false,
      });
      if (mapInstanceRef.current) {
        newPolygon.setMap(mapInstanceRef.current);
        setCurrentPolygon(newPolygon);
      }
      toastHandler({
        title: "Éxito",
        description: "Polígono dibujado correctamente.",
        variant: "success",
      });
      // Scroll al header y animar el botón de guardar cambios
      const header = document.getElementById("branch-detail-header");
      if (header) {
        header.scrollIntoView({ behavior: "smooth", block: "start" });
        const saveButton = document.getElementById("savebutton");
        if (saveButton) {
          saveButton.style.animation = "pulse 1s ease-in-out";
          setTimeout(() => {
            saveButton.style.animation = "";
          }, 900);
          setTimeout(() => {
            saveButton.style.animation = "pulse 1s ease-in-out";
          }, 1000);
          setTimeout(() => {
            saveButton.style.animation = "";
          }, 2000);
        }
      }
      setPolygonDirty(true);
      toastHandler({
        description:
          "Recuerda guardar los cambios arriba para aplicar el polígono.",
        variant: "warning",
      });
    },
    [editMode, currentPolygon],
  );

  const handleEditPolygon = () => {
    setEditMode(true);

    const hasExistingPolygon =
      (branchData?.polygon && branchData.polygon.length > 0) ||
      (drawnPolygon && drawnPolygon.length > 0);

    if (!hasExistingPolygon) {
      toastHandler({
        title: "Error",
        description: "No hay un polígono existente para editar.",
        variant: "destructive",
      });
      return;
    }

    // Si hay un polígono existente, solo habilitamos la edición
    if (currentPolygon) {
      currentPolygon.setEditable(true);
      currentPolygon.setDraggable(true);
    }
  };

  const handleRemoveDriver = async (driverId: number) => {
    setLoadingDrivers(true);
    try {
      await updateDriver(driverId, {
        branch_id: null,
      });

      toastHandler({
        description: "Conductor eliminado correctamente.",
        variant: "success",
      });

      // Refresh the drivers list
      fetchBranchDrivers();
    } catch (error) {
      console.error("Error removing driver:", error);
      toastHandler({
        title: "Error",
        description: "No se pudo eliminar el conductor.",
        variant: "destructive",
      });
    } finally {
      setLoadingDrivers(false);
    }
  };

  const handleCreateDefaultPolygon = () => {
    setEditMode(true);
    if (currentPolygon) {
      currentPolygon.setMap(null);
      setCurrentPolygon(null);
    }
    const center = {
      lat: branchData?.location_lat || 0,
      lng: branchData?.location_lon || 0,
    };
    // Create a small square (0.003 deg offset)
    const offset = 0.003;
    const defaultPolygonCoords = [
      { lat: center.lat + offset, lng: center.lng - offset },
      { lat: center.lat + offset, lng: center.lng + offset },
      { lat: center.lat - offset, lng: center.lng + offset },
      { lat: center.lat - offset, lng: center.lng - offset },
      { lat: center.lat + offset, lng: center.lng - offset },
    ];
    setDrawnPolygon(defaultPolygonCoords);
    setBranchData({ ...branchData, polygon: defaultPolygonCoords });
    setPolygonDirty(true);
    if (mapInstanceRef.current) {
      const newPolygon = new window.google.maps.Polygon({
        paths: defaultPolygonCoords,
        strokeColor: "#FF0000",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#FF0000",
        fillOpacity: 0.35,
        editable: true,
        draggable: true,
        clickable: false,
      });
      newPolygon.setMap(mapInstanceRef.current);
      setCurrentPolygon(newPolygon);
    }
    toastHandler({
      title: "Polígono creado",
      description:
        "Se ha creado un polígono por defecto. Puedes editarlo directamente en el mapa.",
      variant: "success",
    });
    setTimeout(() => {
      toastHandler({
        description:
          "Recuerda guardar los cambios arriba para aplicar el polígono.",
        variant: "warning",
      });
    }, 1000);
  };

  // Validation function
  const validateBranchData = () => {
    const errors: Record<string, string> = {};
    try {
      // Validate using Zod schema
      branchSchema.parse({
        code: branchData?.code || "",
        name: branchData?.name || "",
        country_code: branchData?.country_code || "",
        phone: branchData?.phone || "",
        full_address: branchData?.full_address || "",
        location_lat: branchData?.location_lat || 0,
        location_lon: branchData?.location_lon || 0,
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        error.errors.forEach((err) => {
          if (err.path.length > 0) {
            errors[err.path[0] as string] = err.message;
          }
        });
      }
    }
    // Remove polygon validation from here
    setValidationErrors(errors);
    return Object.keys(errors).length === 0 ? null : errors;
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      // Check if only order_grouping_enabled has changed
      const onlyOrderGroupingChanged =
        !isCreateMode &&
        originalBranchData &&
        branchData &&
        originalBranchData.order_grouping_enabled !==
          branchData.order_grouping_enabled &&
        originalBranchData.name === branchData.name &&
        originalBranchData.code === branchData.code &&
        originalBranchData.is_active === branchData.is_active &&
        originalBranchData.country_code === branchData.country_code &&
        originalBranchData.phone === branchData.phone &&
        originalBranchData.full_address === branchData.full_address &&
        originalBranchData.location_lon === branchData.location_lon &&
        originalBranchData.location_lat === branchData.location_lat &&
        !polygonDirty;

      // If only order grouping changed, use the specific endpoint
      if (onlyOrderGroupingChanged && branch?.id) {
        await toggleOrderGrouping(branch.id, branchData.order_grouping_enabled);
        setOriginalBranchData({
          ...originalBranchData,
          order_grouping_enabled: branchData.order_grouping_enabled,
        });
        setEditMode(false);
        setValidationErrors({});
        setSaving(false);
        return;
      }

      // Always get the latest polygon from the map before saving
      let latestPolygonCoords = drawnPolygon;
      if (currentPolygon) {
        const path = currentPolygon.getPath();
        const coordinates: google.maps.LatLngLiteral[] = [];
        for (let i = 0; i < path.getLength(); i++) {
          const point = path.getAt(i);
          coordinates.push({ lat: point.lat(), lng: point.lng() });
        }
        latestPolygonCoords = coordinates;
      }
      // Polygon validation here
      if (!latestPolygonCoords || latestPolygonCoords.length === 0) {
        // Scroll to the polygon section if the polygon is missing
        const polygonSection = document.getElementById("poligon");
        if (polygonSection) {
          polygonSection.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }

        toastHandler({
          title: "Error de validación",
          description: "Debes dibujar un polígono de cobertura",
          variant: "destructive",
        });
        setSaving(false);
        return;
      }
      setDrawnPolygon(latestPolygonCoords);
      setBranchData((prev) => ({ ...prev, polygon: latestPolygonCoords }));

      const errors = validateBranchData();
      if (errors) {
        Object.values(errors).forEach((error) => {
          toastHandler({
            title: "Error de validación",
            description: error,
            variant: "destructive",
          });
        });
        setSaving(false);
        return;
      }

      // Convert polygon to GeoJSON string for serviceZonePolygon
      const polygonCoords = latestPolygonCoords.map((point) => ({
        lat: point.lat,
        lon: point.lng,
      }));
      // Ensure the polygon is closed (first and last point are the same)
      if (
        polygonCoords.length > 0 &&
        (polygonCoords[0].lat !== polygonCoords[polygonCoords.length - 1].lat ||
          polygonCoords[0].lon !== polygonCoords[polygonCoords.length - 1].lon)
      ) {
        polygonCoords.push({ ...polygonCoords[0] });
      }

      const serviceZonePolygon = polygonCoords;

      // Prepare branch payload (include all relevant fields)
      const branchPayload = {
        name: branchData?.name || "",
        code: branchData?.code || "",
        is_active: branchData?.is_active ?? true,
        order_grouping_enabled: branchData?.order_grouping_enabled ?? true,
        country_code: branchData?.country_code || "",
        phone: branchData?.phone || "",
        full_address: branchData?.full_address || "",
        location_lon: branchData?.location_lon || 0,
        location_lat: branchData?.location_lat || 0,
        brand_id: branchData?.brand_id || Number(brandId) || 0,
        created_at: branchData?.created_at || new Date().toISOString(),
        // Add other fields as needed
      };

      const payload = {
        branch: branchPayload,
        serviceZonePolygon, // a valid GeoJSON Polygon string
      };

      if (isCreateMode) {
        const newBranch = await createBranch(payload as any);
        setPolygonDirty(false);

        // Redirigir a la página de la nueva sucursal creada
        if (newBranch && newBranch.id) {
          setEditMode(false);
          // Redirigir a la página de detalle de la nueva sucursal
          navigate(`/brands/${brandId}/branches/${newBranch.id}`, {
            replace: true,
          });
          return; // Salir temprano para evitar ejecutar el resto del código
        }
      } else if (branch) {
        await updateBranch(Number(branchId), payload as any);
        setBranchData({
          ...branchData,
          ...payload.branch,
        });
        setOriginalBranchData({
          ...branchData,
          ...payload.branch,
        });
        setPolygonDirty(false);
      }
      setEditMode(false);
      setValidationErrors({}); // Clear validation errors on successful save
    } finally {
      setSaving(false);
    }
  };
  const handleCancelEdit = () => {
    if (window.location.pathname.includes("new")) {
      navigate(`/brands/${brandId}`);
      return;
    }
    if (originalBranchData) {
      setBranchData(originalBranchData);
    }
    setEditMode(false);
    setValidationErrors({}); // Clear validation errors on cancel
  };

  // Fetch paginated drivers for this branch with filters
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const fetchBranchDrivers = useCallback(async () => {
    // Validate that we have the necessary branch data
    if (!branch?.id) {
      setBranchDrivers([]);
      setDriversTotalCount(0);
      setLoadingDrivers(false);
      return;
    }

    setLoadingDrivers(true);
    try {
      const filters: any = {
        page: currentPage,
        limit: PAGE_SIZE,
        branch_id: branch.id, // Always filter by current branch ID
        active: true, // Only fetch active drivers
      };

      // Add search filter if provided
      if (debouncedSearch.trim()) {
        filters.search = debouncedSearch.trim();
      }

      const response = await fetchDriversPaginated(filters);

      if (response && typeof response === "object" && "data" in response) {
        setBranchDrivers(response.data || []);
        setDriversTotalCount(response.total || 0);
      } else if (Array.isArray(response)) {
        setBranchDrivers(response);
        setDriversTotalCount(response.length);
      } else {
        setBranchDrivers([]);
        setDriversTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching branch drivers:", error);
      setBranchDrivers([]);
      setDriversTotalCount(0);
    } finally {
      setLoadingDrivers(false);
    }
  }, [branch?.id, currentPage, debouncedSearch]);

  // Reset page to 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  // Fetch drivers when branch, page, filters or search change
  useEffect(() => {
    if (!isCreateMode && branch?.id) {
      fetchBranchDrivers();
    }
  }, [isCreateMode, branch?.id, currentPage, debouncedSearch]);
  const totalPages = Math.ceil(driversTotalCount / PAGE_SIZE);

  // In the component state:
  const [assignSearch, setAssignSearch] = useState("");

  // Add debounced search for assign modal
  const debouncedAssignSearch = useDebounce(assignSearch, 400);

  // Function to fetch available drivers for assign modal
  const fetchAvailableDrivers = useCallback(async () => {
    if (!branch?.id) return;

    setLoadingAssignDrivers(true);
    try {
      // Fetch all active drivers without pagination limits
      const filters: any = {
        page: 1,
        limit: 999999, // Remove practical limit to get ALL drivers
        active: true,
      };

      if (debouncedAssignSearch.trim()) {
        filters.search = debouncedAssignSearch.trim();
      }

      const response = await fetchDriversPaginated(filters);

      let allDrivers: Driver[] = [];

      if (response && typeof response === "object" && "data" in response) {
        allDrivers = response.data || [];
      } else if (Array.isArray(response)) {
        allDrivers = response;
      }

      // Filter out drivers that belong to the current branch
      const availableDrivers = allDrivers.filter(
        (driver: Driver) => driver.branch_id !== branch.id,
      );

      // Show all available drivers (no pagination needed in modal)
      setAssignDriversData(availableDrivers);
    } catch (error) {
      console.error("Error fetching available drivers:", error);
      setAssignDriversData([]);
    } finally {
      setLoadingAssignDrivers(false);
    }
  }, [branch?.id, debouncedAssignSearch]);

  // Fetch available drivers when modal opens or filters change
  useEffect(() => {
    if (isAssignModalOpen && branch?.id) {
      fetchAvailableDrivers();
    }
  }, [isAssignModalOpen, branch?.id, debouncedAssignSearch]);

  if (error) return <ErrorMessage message={error} />;

  // Access control: Show access denied if user doesn't have permission
  if (shouldShowAccessDenied) {
    return <BranchAccessDenied />;
  }

  if (!branch && !isCreateMode) {
    return loading ? (
      <LoadingSpinner />
    ) : (
      <EmptyState
        icon={Store}
        title="Sucursal no encontrada"
        description="La sucursal que buscas no existe o ha sido eliminada"
        action={{
          label: "Volver a sucursales",
          onClick: () => navigate(`/brands/${brandId}`),
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 w-full">
      {loading && (
        <div className="fixed bg-white/50 inset-0 flex items-center justify-center z-50">
          <LoadingSpinner />
        </div>
      )}
      {/* Header - Enhanced responsiveness */}
      <header
        className="bg-white px-4 sm:px-6 py-4 sm:py-6 w-full shadow-sm"
        id="branch-detail-header"
      >
        <div className="max-w-7xl mx-auto">
          {/* Mobile-first approach: allow wrapping on very small screens */}
          <h1 className="text-lg leading-tight sm:text-xl md:text-2xl font-semibold text-gray-900 break-words sm:truncate">
            <span>
              {brandName.charAt(0).toUpperCase() + brandName.slice(1)}
            </span>
            {brandCountry && <CountryBadge country={brandCountry} />}
          </h1>
          {/* Improved breadcrumbs for mobile */}
          <nav className="flex items-center gap-1 text-sm text-gray-600 mt-2 overflow-x-auto">
            <Link
              to="/brands"
              className="text-gray-500 hover:text-indigo-600 transition-colors whitespace-nowrap"
            >
              Negocios
            </Link>
            <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <Link
              to={`/brands/${brandId}`}
              className="text-gray-500 hover:text-indigo-600 transition-colors whitespace-nowrap"
            >
              {brandName.charAt(0).toUpperCase() + brandName.slice(1)}
            </Link>
            <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
            {/* Allow branch name to wrap on very small screens */}
            <span className="font-medium text-gray-900 break-words sm:truncate">
              {isCreateMode ? "Nueva sucursal" : branch?.name}
            </span>
          </nav>
        </div>
      </header>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Information Section Header */}
        <Card className="bg-white shadow-sm border border-gray-200">
          <div className="p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="font-semibold text-gray-900">Información</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Información y datos sobre la sucursal
                </p>
              </div>
            </div>
            {window.location.pathname.includes("new") ? (
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full sm:w-auto text-gray-600 hover:text-gray-900 hover:bg-gray-100 border border-gray-300"
                  onClick={handleCancelEdit}
                >
                  Cancelar
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className={`w-full sm:w-auto bg-indigo-600 text-white hover:bg-indigo-700 ${polygonDirty ? "animate-pulse" : ""}`}
                  onClick={handleSaveChanges}
                  id="savebutton"
                >
                  <div className="flex items-center gap-2 justify-center">
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    <span>
                      {isCreateMode && saving
                        ? "Creando..."
                        : isCreateMode && !saving
                          ? "Crear sucursal"
                          : saving
                            ? "Guardando..."
                            : "Guardar cambios"}
                    </span>
                  </div>
                </Button>
              </div>
            ) : editMode ? (
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full sm:w-auto text-gray-600 hover:text-gray-900 hover:bg-gray-100 border border-gray-300"
                  onClick={handleCancelEdit}
                >
                  Cancelar
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className={`w-full sm:w-auto bg-indigo-600 text-white hover:bg-indigo-700 ${polygonDirty ? "animate-pulse" : ""}`}
                  onClick={handleSaveChanges}
                  id="savebutton"
                >
                  <div className="flex items-center gap-2 justify-center">
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    <span>
                      {isCreateMode && saving
                        ? "Creando..."
                        : isCreateMode && !saving
                          ? "Crear sucursal"
                          : saving
                            ? "Guardando..."
                            : "Guardar cambios"}
                    </span>
                  </div>
                </Button>
              </div>
            ) : (
              <Button
                variant="default"
                size="sm"
                className="w-full sm:w-auto bg-indigo-600 text-white hover:bg-indigo-700"
                onClick={() => setEditMode(true)}
              >
                <div className="flex items-center gap-2 justify-center">
                  <Pencil className="w-4 h-4" />
                  <span>Editar</span>
                </div>
              </Button>
            )}
          </div>
        </Card>
        {/* Information Section */}
        <Card className="bg-white shadow-sm border border-gray-200">
          {/* Toggle for Order Grouping */}
          <div className="p-3 sm:p-4 lg:p-6 xl:p-8">
            <div className="space-y-3 mb-4 sm:mb-6 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex flex-col xs:flex-row xs:items-start gap-3 xs:gap-4">
                <div className="flex-1 min-w-0">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Agrupación de órdenes
                  </label>
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                    Permitir agrupar órdenes cercanas en una misma entrega para
                    optimizar las rutas de reparto
                  </p>
                </div>
                <div className="flex-shrink-0 self-start xs:self-auto">
                  <button
                    type="button"
                    onClick={() => {
                      if (editMode) {
                        setBranchData({
                          ...branchData,
                          order_grouping_enabled:
                            !branchData.order_grouping_enabled,
                        });
                      }
                    }}
                    className={`relative inline-flex h-6 w-11 rounded-full border-2 border-transparent focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-none transition-all duration-200 ease-in-out ${
                      branchData?.order_grouping_enabled
                        ? "bg-indigo-600 hover:bg-indigo-700"
                        : "bg-gray-300 hover:bg-gray-400"
                    } ${!editMode ? "opacity-50 cursor-not-allowed hover:bg-gray-300" : "cursor-pointer"}`}
                    role="switch"
                    aria-checked={branchData?.order_grouping_enabled}
                    aria-label="Alternar agrupación de órdenes"
                    disabled={!editMode}
                  >
                    <span
                      className={`inline-block h-5 w-5 rounded-full bg-white shadow-lg transform transition-transform duration-200 ease-in-out ${
                        branchData?.order_grouping_enabled
                          ? "translate-x-5"
                          : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              </div>
              {branchData?.order_grouping_enabled && (
                <div className="mt-3 p-3 bg-indigo-50 rounded-md border border-indigo-200">
                  <div className="flex items-start sm:items-center gap-2">
                    <svg
                      className="h-4 w-4 text-indigo-600 flex-shrink-0 mt-0.5 sm:mt-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-xs text-indigo-700 leading-relaxed">
                      Las órdenes dentro de un radio configurable podrán ser
                      agrupadas automáticamente
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
              <div className="space-y-1">
                <label
                  htmlFor="code"
                  className="block text-sm font-medium text-gray-700"
                >
                  Código <span className="text-red-500">*</span>
                </label>
                <input
                  id="code"
                  type="text"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm ${
                    validationErrors.code ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="Código"
                  value={branchData?.code || ""}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setBranchData({ ...branchData, code: e.target.value })
                  }
                  disabled={!editMode}
                />
                {validationErrors.code && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.code}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm ${
                    validationErrors.name ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="Nombre"
                  value={branchData?.name || ""}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setBranchData({ ...branchData, name: e.target.value })
                  }
                  disabled={!editMode}
                />
                {validationErrors.name && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.name}
                  </p>
                )}
              </div>
              <div className="space-y-1 sm:col-span-2 lg:col-span-1">
                <label
                  htmlFor="country_code"
                  className="block text-sm font-medium text-gray-700"
                >
                  Código de país <span className="text-red-500">*</span>
                </label>
                <input
                  id="country_code"
                  type="text"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm ${
                    validationErrors.country_code
                      ? "border-red-300"
                      : "border-gray-300"
                  }`}
                  placeholder="Código de país"
                  maxLength={3}
                  value={branchData?.country_code || ""}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const numeric = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 3);
                    setBranchData({ ...branchData, country_code: numeric });
                  }}
                  disabled={!editMode}
                />
                {validationErrors.country_code && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.country_code}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:gap-4 lg:gap-6 mt-3 sm:mt-4">
              <div className="space-y-1">
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700"
                >
                  Teléfono <span className="text-red-500">*</span>
                </label>
                <input
                  id="phone"
                  type="text"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm ${
                    validationErrors.phone
                      ? "border-red-300"
                      : "border-gray-300"
                  }`}
                  placeholder="Teléfono"
                  value={branchData?.phone || ""}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setBranchData({ ...branchData, phone: e.target.value })
                  }
                  disabled={!editMode}
                />
                {validationErrors.phone && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors.phone}
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-1 mt-3 sm:mt-4">
              <LocationPicker
                latitude={branchData?.location_lat || 0}
                longitude={branchData?.location_lon || 0}
                onLocationChange={(lat, lng) => {
                  setBranchData((prev) => ({
                    ...prev,
                    location_lat: lat,
                    location_lon: lng,
                  }));
                }}
                onAddressChange={(address) => {
                  setBranchData((prev) => ({
                    ...prev,
                    full_address: address,
                  }));
                }}
                disabled={!editMode}
                placeholder={{
                  lat: "13.6929",
                  lng: "-89.2182",
                  address: "Buscar dirección de la sucursal...",
                }}
                mapHeight="250px"
                showAddressSearch={true}
                addressValue={branchData?.full_address || ""}
              />
              {(validationErrors.location_lat ||
                validationErrors.location_lon) && (
                <div className="space-y-1">
                  {validationErrors.location_lat && (
                    <p className="text-red-500 text-xs">
                      {validationErrors.location_lat}
                    </p>
                  )}
                  {validationErrors.location_lon && (
                    <p className="text-red-500 text-xs">
                      {validationErrors.location_lon}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </Card>
        <div className="border-b border-gray-200"></div>

        {/* Coverage Section */}
        <Card className="bg-white shadow-sm border border-gray-200">
          <div className="p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                  />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="font-semibold text-gray-900">Cobertura</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Define el área de cobertura de la sucursal
                </p>
              </div>
            </div>
          </div>
        </Card>
        <Card
          className="bg-white shadow-sm border border-gray-200"
          id="poligon"
        >
          <div className="p-4 sm:p-6">
            <div className="flex flex-col space-y-4 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {(branchData?.polygon && branchData.polygon.length > 0) ||
                    (drawnPolygon && drawnPolygon.length > 0)
                      ? "Polígono de cobertura definido"
                      : "Dibuja el polígono de cobertura"}
                  </p>
                  {editMode && (
                    <p className="text-xs text-emerald-600 mt-1">
                      Usa las herramientas del mapa para dibujar el área de
                      cobertura
                    </p>
                  )}
                </div>

                {/* Polygon controls - responsive */}
                {editMode ? (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={handleCreateDefaultPolygon}
                      className="flex items-center justify-center gap-2 px-3 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
                      type="button"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                      <span>Crear nuevo</span>
                    </button>
                    <button
                      onClick={() => {
                        const saveButton =
                          document.getElementById("savebutton");
                        if (saveButton) {
                          saveButton.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          });
                        }
                        toastHandler({
                          title: "Guarda los cambios",
                          description:
                            "El polígono se guardara al clickear el botón de guardar cambios",
                          variant: "info",
                        });
                      }}
                      className="flex items-center justify-center gap-2 px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                      type="button"
                    >
                      <span>Guardar</span>
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={handleEditPolygon}
                      className="flex items-center justify-center gap-2 px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                      type="button"
                    >
                      <span>Editar polígono</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Map container - responsive */}
            <div className="relative w-full h-64 sm:h-80 lg:h-96 rounded-lg overflow-hidden border border-gray-200">
              <GoogleMapWrapper
                center={center}
                zoom={16}
                mapContainerStyle={containerStyle}
                mapId="4dc53f1be23f15bfe70ca0ef"
                onLoad={handleMapLoad}
              >
                {/* Mostrar DrawingManager solo en modo edición */}
                {editMode &&
                  typeof window !== "undefined" &&
                  window.google?.maps?.drawing && (
                    <DrawingManager
                      options={{
                        drawingControl: true,
                        drawingControlOptions: {
                          position:
                            window.google.maps.ControlPosition.TOP_CENTER,
                          drawingModes: [
                            window.google.maps.drawing.OverlayType.POLYGON,
                          ],
                        },
                        polygonOptions: {
                          fillColor: "#FF0000",
                          fillOpacity: 0.35,
                          strokeColor: "#FF0000",
                          strokeOpacity: 0.8,
                          strokeWeight: 2,
                          editable: true,
                          draggable: true,
                        },
                      }}
                      onPolygonComplete={handlePolygonComplete}
                    />
                  )}
              </GoogleMapWrapper>
            </div>
          </div>
        </Card>
        <div className="border-b border-gray-200"></div>

        {/* Drivers Section - Only show if not in create mode */}
        {!isCreateMode && (
          <>
            <Card className="bg-white shadow-sm border border-gray-200">
              <div className="p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Bike className="h-5 w-5 text-gray-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="font-semibold text-gray-900">
                      Conductores Asignados
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Gestiona y asigna conductores a la sucursal
                    </p>
                  </div>
                </div>
                <button
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                  onClick={handleOpenAssignModal}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  <span>Asignar</span>
                </button>
              </div>
            </Card>
            <Card className="bg-white shadow-sm border border-gray-200 min-h-[330px]">
              {/* Enhanced search section */}
              <div className="p-4 sm:p-6 border-b border-gray-100">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1 max-w-md">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Buscar conductor por nombre..."
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      <svg
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div ref={tableRef}>
                {loadingDrivers ? (
                  <div className="p-6 text-center flex flex-col h-72 justify-center items-center">
                    <Loader2 className="w-6 h-6 mb-2 animate-spin text-indigo-600" />
                    <span className="text-gray-600">
                      Cargando conductores...
                    </span>
                  </div>
                ) : errorDrivers ? (
                  <div className="p-6 text-center text-red-500">
                    {errorDrivers}
                  </div>
                ) : branchDrivers.length === 0 ? (
                  <EmptyState
                    title="Sin conductores asignados"
                    description="No hay conductores asignados a esta sucursal"
                    illustration={{
                      src: "/driver-illustration/empty-driver.svg",
                      alt: "Sin conductores asignados",
                    }}
                  />
                ) : (
                  <div className="p-4 sm:p-6">
                    <div className="hidden lg:block overflow-x-auto">
                      <OrdersTable
                        columns={[
                          { header: "Nombre", key: "name" },
                          { header: "Vehículo", key: "vehicle" },
                          {
                            header: "Último acceso",
                            key: "last_time_connected",
                          },
                          { header: "Contrato", key: "contract" },
                          { header: "Estado", key: "status" },
                          { header: "Acciones", key: "actions" },
                        ]}
                        children={branchDrivers.map((driver) => (
                          <tr
                            key={driver.id}
                            onClick={() => {
                              navigate(`/drivers/${driver.id}`);
                            }}
                            className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                          >
                            <td className="py-4 px-4">
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-semibold">
                                  <img
                                    className="w-8 h-8 rounded-full object-cover"
                                    src={
                                      driver.picture ||
                                      "/driver-illustration/profile-pic-default.svg"
                                    }
                                    alt={`Avatar de ${driver.name}`}
                                  />
                                </div>
                                <span className="font-medium text-gray-900">
                                  {driver.name || "Sin nombre"}
                                </span>
                              </div>
                            </td>
                            <td className="py-4 px-4 text-gray-700">
                              {driver.vehicle_type === "moto" ||
                              driver.vehicle_type === "motorcycle"
                                ? "Moto"
                                : driver.vehicle_type === "bicycle"
                                  ? "Bicicleta"
                                  : driver.vehicle_type === "car"
                                    ? "Automóvil"
                                    : "N/A"}
                            </td>
                            <td className="py-4 px-4 text-gray-700">
                              {driver.last_time_connected
                                ? new Date(
                                    driver.last_time_connected,
                                  ).toLocaleDateString("es-ES", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                  }) +
                                  " - " +
                                  new Date(
                                    driver.last_time_connected,
                                  ).toLocaleTimeString("es-ES", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                  })
                                : "N/A"}
                            </td>
                            <td className="py-4 px-4 text-gray-700">
                              {driver.worker_type === "full_time"
                                ? "Full-time"
                                : driver.worker_type === "part_time"
                                  ? "Part-time"
                                  : driver.worker_type
                                    ? driver.worker_type
                                        .charAt(0)
                                        .toUpperCase() +
                                      driver.worker_type.slice(1)
                                    : "N/A"}
                            </td>
                            <td className="py-4 px-4">
                              <span
                                className={`text-green-600 font-medium rounded-lg text-sm px-2 py-1 ${
                                  !driver.driver_working &&
                                  "bg-red-50 text-red-500"
                                }`}
                              >
                                {driver.driver_working
                                  ? "Disponible"
                                  : "No disponible"}
                              </span>
                            </td>
                            {driver.id !== 19 ? (
                              <td className="py-4 px-4">
                                <div className="flex items-center space-x-2">
                                  {hasPermission("driver:update") && (
                                    <DriverModal
                                      trigger={
                                        <button
                                          type="button"
                                          className="text-sm text-gray-500 p-2 rounded-lg hover:bg-gray-100 hover:text-gray-600"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <Pencil className="w-4 h-4" />
                                        </button>
                                      }
                                      driverToEdit={driver}
                                      refreshDrivers={async () => {
                                        fetchBranchDrivers();
                                      }}
                                    />
                                  )}

                                  <button
                                    className={`text-sm text-gray-500 p-2 rounded-lg hover:bg-gray-100 hover:text-gray-600`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveDriver(driver.id);
                                    }}
                                    title={
                                      driver.active
                                        ? "Desasignar conductor"
                                        : "Asignar conductor"
                                    }
                                  >
                                    {driver.active ? (
                                      <XCircle className="w-4 h-4" />
                                    ) : (
                                      <CheckCircle2 className="w-4 h-4" />
                                    )}
                                  </button>
                                </div>
                              </td>
                            ) : (
                              <td className="py-4 px-4">
                                <div className="flex items-center space-x-2">
                                  <span className="text-gray-500">-</span>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                        emptyMessage="No hay conductores asignados"
                      />
                    </div>

                    {/* Mobile/Tablet card layout - Enhanced responsiveness */}
                    <div className="lg:hidden">
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                        {branchDrivers.map((driver) => (
                          <Card
                            key={driver.id}
                            className="bg-white border border-gray-200 hover:shadow-md transition-shadow"
                            onClick={() => {
                              navigate(`/drivers/${driver.id}`);
                            }}
                          >
                            <div className="p-4 flex flex-col gap-4">
                              {/* Header with avatar and actions */}
                              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                {/* Avatar & Name */}
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className="relative w-12 h-12">
                                    <img
                                      className="w-12 h-12 rounded-full object-cover shadow-sm"
                                      src={
                                        driver.picture ||
                                        "/driver-illustration/profile-pic-default.svg"
                                      }
                                      alt={`Avatar de ${driver.name}`}
                                      onError={(e) => {
                                        const target =
                                          e.target as HTMLImageElement;
                                        target.style.display = "none";
                                        target.nextElementSibling!.textContent =
                                          driver.name
                                            ? driver.name
                                                .split(" ")
                                                .map((n) => n[0])
                                                .join("")
                                                .toUpperCase()
                                            : "NA";
                                      }}
                                    />
                                    <span className="hidden">
                                      {driver.name
                                        ? driver.name
                                            .split(" ")
                                            .map((n) => n[0])
                                            .join("")
                                            .toUpperCase()
                                        : "NA"}
                                    </span>
                                  </div>
                                  <div className="min-w-0">
                                    <h3 className="font-semibold text-gray-900 text-base leading-tight whitespace-normal break-words">
                                      {driver.name || "Sin nombre"}
                                    </h3>
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 mt-1 rounded-full text-xs font-medium ${
                                        driver.active
                                          ? "bg-green-100 text-green-800"
                                          : "bg-red-100 text-red-800"
                                      }`}
                                    >
                                      {driver.active
                                        ? "Disponible"
                                        : "No disponible"}
                                    </span>
                                  </div>
                                </div>

                                {/* Action buttons */}
                                {driver.id !== 19 && (
                                  <div className="flex items-center gap-1 self-start">
                                    {hasPermission("driver:update") && (
                                      <DriverModal
                                        trigger={
                                          <button
                                            type="button"
                                            className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                            onClick={(e) => e.stopPropagation()}
                                            title="Editar conductor"
                                          >
                                            <Pencil className="w-4 h-4" />
                                          </button>
                                        }
                                        driverToEdit={driver}
                                        refreshDrivers={async () => {
                                          fetchBranchDrivers();
                                        }}
                                      />
                                    )}
                                    <button
                                      className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveDriver(driver.id);
                                      }}
                                      title={
                                        driver.active
                                          ? "Eliminar conductor"
                                          : "Activar conductor"
                                      }
                                    >
                                      {driver.active ? (
                                        <XCircle className="w-4 h-4" />
                                      ) : (
                                        <CheckCircle2 className="w-4 h-4" />
                                      )}
                                    </button>
                                  </div>
                                )}
                              </div>

                              {/* Info section */}
                              <div className="text-sm space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <span className="text-gray-500 font-medium">
                                      Vehículo
                                    </span>
                                    <p className="text-gray-900 mt-1">
                                      {driver.vehicle_type === "moto" ||
                                      driver.vehicle_type === "motorcycle"
                                        ? "🏍️ Moto"
                                        : driver.vehicle_type === "bicycle"
                                          ? "🚲 Bicicleta"
                                          : driver.vehicle_type === "car"
                                            ? "🚗 Automóvil"
                                            : "❓ N/A"}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-gray-500 font-medium">
                                      Contrato
                                    </span>
                                    <p className="text-gray-900 mt-1">
                                      {driver.worker_type === "full_time"
                                        ? "Full-time"
                                        : driver.worker_type === "part_time"
                                          ? "Part-time"
                                          : driver.worker_type
                                            ? driver.worker_type
                                                .charAt(0)
                                                .toUpperCase() +
                                              driver.worker_type.slice(1)
                                            : "N/A"}
                                    </p>
                                  </div>
                                </div>

                                <div className="pt-2 border-t border-gray-100">
                                  <span className="text-gray-500 font-medium">
                                    Agregado
                                  </span>
                                  <p className="text-gray-900 mt-1">
                                    {driver.created_at
                                      ? new Date(
                                          driver.created_at,
                                        ).toLocaleDateString("es-ES", {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                        })
                                      : "N/A"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>

                    {/* Pagination and stats - Enhanced layout */}
                    {totalPages > 1 && (
                      <div className="mt-6 pt-4 border-t border-gray-100">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                          <div className="text-sm text-gray-600 order-2 sm:order-1">
                            {driversTotalCount > 0 && (
                              <>
                                Mostrando página{" "}
                                <span className="font-medium">
                                  {currentPage}
                                </span>{" "}
                                de{" "}
                                <span className="font-medium">
                                  {totalPages}
                                </span>{" "}
                                (
                                <span className="font-medium">
                                  {driversTotalCount}
                                </span>{" "}
                                conductores en total)
                              </>
                            )}
                          </div>
                          <div className="order-1 sm:order-2 flex justify-center sm:justify-end">
                            <Pagination
                              totalPages={totalPages}
                              currentPage={currentPage}
                              onPageChange={handlePageChange}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>

            {/* Assign Driver Modal */}
            {isAssignModalOpen && (
              <Dialog
                open={isAssignModalOpen}
                onOpenChange={(v) => {
                  if (!v) handleCloseAssignModal();
                }}
              >
                <DialogContent className="w-screen h-screen max-w-none mx-0 rounded-none sm:max-w-lg sm:w-full sm:mx-4 sm:max-h-[90vh] sm:h-auto sm:rounded-xl p-0 shadow-2xl border-0 bg-white overflow-hidden flex flex-col">
                  {/* 
                    Make the modal content a flex column, 
                    and ensure the scrollable area is between header and footer.
                    Footer is always at the bottom, even on mobile.
                  */}
                  {/* Header - Fixed */}
                  <DialogHeader className="px-4 py-4 sm:px-6 sm:py-5 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-blue-50 flex-shrink-0">
                    <DialogTitle className="text-center text-lg sm:text-xl font-semibold mb-1 text-gray-900">
                      Asignar conductor
                    </DialogTitle>
                    <div className="text-center text-gray-600 text-sm sm:text-base">
                      <span className="font-semibold">Sucursal</span> -{" "}
                      {brandName}
                    </div>
                  </DialogHeader>

                  {/* Search input - Fixed */}
                  <div className="px-4 py-4 sm:px-6 border-b border-gray-100 flex-shrink-0">
                    <input
                      type="text"
                      placeholder="Buscar conductor..."
                      className="border border-gray-300 px-3 py-2.5 rounded-lg text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      value={assignSearch}
                      onChange={(e) => setAssignSearch(e.target.value)}
                    />
                  </div>

                  {/* Content - Scrollable list only */}
                  <div
                    className="flex-1 overflow-y-auto"
                    style={{
                      // On mobile, ensure the content is scrollable and the footer is always visible
                      minHeight: 0,
                    }}
                  >
                    {loadingAssignDrivers ? (
                      <div className="text-center py-8">
                        <Loader2 className="w-6 h-6 mb-2 animate-spin text-indigo-600 mx-auto" />
                        <p className="text-gray-500 text-sm">
                          Cargando conductores...
                        </p>
                      </div>
                    ) : assignDriversData.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <p className="text-sm">
                          {assignSearch.length > 0
                            ? "No se encontraron conductores"
                            : "No hay conductores disponibles"}
                        </p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {assignDriversData.map((driver) => {
                          if (driver.id === 19) return null;
                          return (
                            <label
                              key={driver.id}
                              className={`flex items-center gap-3 p-4 cursor-pointer transition-colors ${selectedDriverIds.includes(driver.id) ? "bg-indigo-50" : "hover:bg-gray-50"}`}
                              htmlFor={`driver-checkbox-${driver.id}`}
                            >
                              <input
                                id={`driver-checkbox-${driver.id}`}
                                type="checkbox"
                                checked={selectedDriverIds.includes(driver.id)}
                                onChange={() => handleDriverSelect(driver.id)}
                                disabled={isAssigning}
                                className="accent-indigo-600 w-4 h-4 sm:w-5 sm:h-5 rounded border-gray-300 focus:ring-indigo-500 disabled:opacity-50"
                              />

                              <img
                                src={
                                  driver.picture ||
                                  "/driver-illustration/profile-pic-default.svg"
                                }
                                alt={driver.name || ""}
                                className="w-8 h-8 sm:w-9 sm:h-9 rounded-full object-cover border border-gray-200 flex-shrink-0"
                              />

                              <div className="flex-1 min-w-0">
                                <span className="font-medium text-gray-900 text-sm sm:text-base truncate block">
                                  {driver.name || ""}
                                </span>
                                <div className="flex flex-wrap items-center gap-2 mt-1">
                                  <span className="text-gray-500 text-xs sm:text-sm">
                                    {driver.vehicle_type === "moto" ||
                                    driver.vehicle_type === "motorcycle"
                                      ? "Moto"
                                      : driver.vehicle_type === "bicycle"
                                        ? "Bicicleta"
                                        : driver.vehicle_type === "car"
                                          ? "Automóvil"
                                          : "N/A"}
                                  </span>
                                  <span
                                    className={`px-2 py-1 rounded text-xs font-medium ${driver.worker_type === "on_demand" ? "bg-indigo-100 text-indigo-700" : "bg-gray-100 text-gray-700"}`}
                                  >
                                    {driver.worker_type === "on_demand"
                                      ? "On-demand"
                                      : driver.worker_type === "full_time"
                                        ? "Full-time"
                                        : driver.worker_type === "part_time"
                                          ? "Part-time"
                                          : driver.worker_type
                                            ? driver.worker_type
                                                .charAt(0)
                                                .toUpperCase() +
                                              driver.worker_type.slice(1)
                                            : "Full-time"}
                                  </span>
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Footer - Fixed and always visible at the bottom */}
                  <DialogFooter className="bg-white border-t border-gray-200 p-3 sm:p-4 flex-shrink-0 min-h-[80px] sm:min-h-[auto]">
                    <div className="flex flex-col gap-2 w-full sm:flex-row sm:gap-3 sm:justify-end">
                      <Button
                        variant="ghost"
                        onClick={handleCloseAssignModal}
                        className="w-full sm:w-auto px-4 py-2.5 h-10 border border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 text-sm font-medium order-1 sm:order-1"
                      >
                        Cancelar
                      </Button>
                      {selectedDriverIds.length > 0 ? (
                        <BranchDriverAssignmentConfirmation
                          trigger={
                            <Button
                              variant="default"
                              disabled={isAssigning}
                              className="w-full sm:w-auto px-4 py-2.5 h-10 bg-indigo-600 text-white hover:bg-indigo-700 transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium order-2 sm:order-2"
                            >
                              {isAssigning ? (
                                <div className="flex items-center justify-center gap-2">
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  <span>Asignando...</span>
                                </div>
                              ) : (
                                `Asignar (${selectedDriverIds.length})`
                              )}
                            </Button>
                          }
                          branch={{
                            id: branchData.id,
                            name: branchData.name,
                            code: branchData.code,
                          }}
                          selectedDriverIds={selectedDriverIds}
                          onSuccess={() => handleConfirmDriverAssignment()}
                          onClose={handleCloseAssignModal}
                        />
                      ) : (
                        <Button
                          variant="default"
                          disabled={true}
                          className="w-full sm:w-auto px-4 py-2.5 h-10 bg-indigo-600 text-white hover:bg-indigo-700 transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium order-2 sm:order-2"
                        >
                          Asignar
                        </Button>
                      )}
                    </div>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </>
        )}
      </div>
    </div>
  );
}
